// Dynamic import for jsPDF to handle cases where it might not be installed
let jsPDF = null;

const loadJsPDF = async () => {
  if (!jsPDF) {
    try {
      const jsPDFModule = await import('jspdf');
      jsPDF = jsPDFModule.default || jsPDFModule;
    } catch (error) {
      throw new Error('PDF generation library not available. Please install jspdf: npm install jspdf');
    }
  }
  return jsPDF;
};

// Helper function to add MahStry branding in top right corner
const addMahStryBranding = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // MahStry text in blue - no rectangle or design
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(59, 130, 246);
  doc.text('Mahstry Private Limited', pageWidth - 10, 15, { align: 'right' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('GSTIN: 08AAUCM5530E1Z9', pageWidth - 10, 20, { align: 'right' });
};

export const generateApplicationReceipt = async (applicationData, institution, transactionId, paymentData) => {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF();
  
  // Add MahStry branding in top right
  addMahStryBranding(doc);
  
  // Company/Platform Name at the top
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Mahstry Private Limited', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Application Receipt', 105, 30, { align: 'center' });
  
  // Line separator
  doc.setLineWidth(0.5);
  doc.line(20, 35, 190, 35);
  
  let yPos = 45;
  
  // Student Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Student Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${applicationData.firstName} ${applicationData.lastName}`, 20, yPos);
  yPos += 6;
  doc.text(`Email: ${applicationData.email}`, 20, yPos);
  yPos += 6;
  doc.text(`Phone: ${applicationData.phone}`, 20, yPos);
  yPos += 6;
  doc.text(`Date of Birth: ${applicationData.dateOfBirth || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Gender: ${applicationData.gender || 'N/A'}`, 20, yPos);
  yPos += 10;
  
  // Address
  if (applicationData.address) {
    doc.text(`Address: ${applicationData.address.street || ''}`, 20, yPos);
    yPos += 6;
    doc.text(`${applicationData.address.city || ''}, ${applicationData.address.state || ''} - ${applicationData.address.pincode || ''}`, 20, yPos);
    yPos += 10;
  }
  
  // Institution Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Institution Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Institution Name
  if (institution.name) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Institution Name:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.name, 60, yPos);
    yPos += 6;
  }
  
  // Institution Type
  if (institution.type) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Type:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.type.charAt(0).toUpperCase() + institution.type.slice(1), 60, yPos);
    yPos += 6;
  }
  
  // Description
  if (institution.description) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Description:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    const description = institution.description.length > 80 
      ? institution.description.substring(0, 80) + '...' 
      : institution.description;
    doc.text(description, 60, yPos);
    yPos += 6;
  }
  
  // Address
  if (institution.address) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Address:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    
    if (institution.address.street) {
      doc.text(institution.address.street, 20, yPos);
      yPos += 6;
    }
    
    const cityState = [
      institution.address.city,
      institution.address.state,
      institution.address.pincode
    ].filter(Boolean).join(', ');
    
    if (cityState) {
      doc.text(cityState, 20, yPos);
      yPos += 6;
    }
  }
  
  // Contact Information
  if (institution.contact) {
    if (institution.contact.phone) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Phone:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.phone, 60, yPos);
      yPos += 6;
    }
    
    if (institution.contact.email) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Email:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.email, 60, yPos);
      yPos += 6;
    }
    
    if (institution.contact.website) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Website:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.website, 60, yPos);
      yPos += 6;
    }
  }
  
  // Additional Info
  if (institution.established) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Established:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.established, 60, yPos);
    yPos += 6;
  }
  
  if (institution.students) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Students:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.students, 60, yPos);
    yPos += 6;
  }
  
  yPos += 10;
  
  // Application Details Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Application Details', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  if (applicationData.previousSchool) {
    doc.text(`Previous School: ${applicationData.previousSchool}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.previousClass) {
    doc.text(`Previous Class: ${applicationData.previousClass}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.applyingForClass) {
    doc.text(`Applying For: ${applicationData.applyingForClass}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.academicYear) {
    doc.text(`Academic Year: ${applicationData.academicYear}`, 20, yPos);
    yPos += 6;
  }
  yPos += 6;
  
  // Parent/Guardian Information
  if (applicationData.parentName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Parent/Guardian Information', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${applicationData.parentName}`, 20, yPos);
    yPos += 6;
    
    if (applicationData.parentPhone) {
      doc.text(`Phone: ${applicationData.parentPhone}`, 20, yPos);
      yPos += 6;
    }
    
    if (applicationData.parentEmail) {
      doc.text(`Email: ${applicationData.parentEmail}`, 20, yPos);
      yPos += 6;
    }
    
    if (applicationData.parentOccupation) {
      doc.text(`Occupation: ${applicationData.parentOccupation}`, 20, yPos);
      yPos += 6;
    }
    yPos += 6;
  }
  
  // Custom Fields Data
  if (applicationData.customFieldsData && Object.keys(applicationData.customFieldsData).length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Additional Information', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    Object.entries(applicationData.customFieldsData).forEach(([fieldLabel, fieldValue]) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      if (fieldValue.url) {
        // File/Photo upload
        doc.text(`${fieldLabel}: ${fieldValue.fileName || 'File uploaded'}`, 20, yPos);
        yPos += 6;
        doc.setFontSize(8);
        doc.text(`File URL: ${fieldValue.url.substring(0, 80)}...`, 20, yPos);
        yPos += 6;
        doc.setFontSize(10);
      } else if (fieldValue.value) {
        // Text/Number/Date field
        const value = typeof fieldValue.value === 'string' 
          ? fieldValue.value.substring(0, 100) 
          : String(fieldValue.value);
        doc.text(`${fieldLabel}: ${value}`, 20, yPos);
        yPos += 6;
      }
    });
    yPos += 6;
  }
  
  // Payment Information Section
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Transaction ID: ${transactionId}`, 20, yPos);
  yPos += 6;
  doc.text(`Application Fee: ₹${paymentData.amount || institution.applicationFee || 0}`, 20, yPos);
  yPos += 6;
  doc.text(`Payment Status: ${paymentData.status || 'Completed'}`, 20, yPos);
  yPos += 6;
  doc.text(`Payment Date: ${new Date().toLocaleDateString('en-IN', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, 20, yPos);
  yPos += 10;
  
  // Footer
  const pageHeight = doc.internal.pageSize.height;
  yPos = pageHeight - 30;
  
  doc.setLineWidth(0.5);
  doc.line(20, yPos, 190, yPos);
  yPos += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a computer-generated receipt. No signature required.', 105, yPos, { align: 'center' });
  yPos += 6;
  doc.text('Thank you for your application!', 105, yPos, { align: 'center' });
  
  // Generate filename
  const fileName = `Application_Receipt_${applicationData.firstName}_${applicationData.lastName}_${Date.now()}.pdf`;
  
  // Save the PDF
  doc.save(fileName);
  
  return fileName;
};

// Generate receipt as blob for viewing (without downloading)
export const generateApplicationReceiptBlob = async (applicationData, institution, transactionId, paymentData) => {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF();
  
  // Add MahStry branding in top right
  addMahStryBranding(doc);
  
  // Company/Platform Name at the top
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Mahstry Private Limited', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Application Receipt', 105, 30, { align: 'center' });
  
  // Line separator
  doc.setLineWidth(0.5);
  doc.line(20, 35, 190, 35);
  
  let yPos = 45;
  
  // Student Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Student Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${applicationData.firstName} ${applicationData.lastName}`, 20, yPos);
  yPos += 6;
  doc.text(`Email: ${applicationData.email}`, 20, yPos);
  yPos += 6;
  doc.text(`Phone: ${applicationData.phone}`, 20, yPos);
  yPos += 6;
  doc.text(`Date of Birth: ${applicationData.dateOfBirth || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Gender: ${applicationData.gender || 'N/A'}`, 20, yPos);
  yPos += 10;
  
  // Address
  if (applicationData.address) {
    doc.text(`Address: ${applicationData.address.street || ''}`, 20, yPos);
    yPos += 6;
    doc.text(`${applicationData.address.city || ''}, ${applicationData.address.state || ''} - ${applicationData.address.pincode || ''}`, 20, yPos);
    yPos += 10;
  }
  
  // Institution Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Institution Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Institution Name
  if (institution.name) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Institution Name:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.name, 60, yPos);
    yPos += 6;
  }
  
  // Institution Type
  if (institution.type) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Type:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.type.charAt(0).toUpperCase() + institution.type.slice(1), 60, yPos);
    yPos += 6;
  }
  
  // Description
  if (institution.description) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Description:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    const description = institution.description.length > 80 
      ? institution.description.substring(0, 80) + '...' 
      : institution.description;
    doc.text(description, 60, yPos);
    yPos += 6;
  }
  
  // Address
  if (institution.address) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Address:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
    
    if (institution.address.street) {
      doc.text(institution.address.street, 20, yPos);
      yPos += 6;
    }
    
    const cityState = [
      institution.address.city,
      institution.address.state,
      institution.address.pincode
    ].filter(Boolean).join(', ');
    
    if (cityState) {
      doc.text(cityState, 20, yPos);
      yPos += 6;
    }
  }
  
  // Contact Information
  if (institution.contact) {
    if (institution.contact.phone) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Phone:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.phone, 60, yPos);
      yPos += 6;
    }
    
    if (institution.contact.email) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Email:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.email, 60, yPos);
      yPos += 6;
    }
    
    if (institution.contact.website) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Website:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(institution.contact.website, 60, yPos);
      yPos += 6;
    }
  }
  
  // Additional Info
  if (institution.established) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Established:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.established, 60, yPos);
    yPos += 6;
  }
  
  if (institution.students) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Students:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(institution.students, 60, yPos);
    yPos += 6;
  }
  
  yPos += 10;
  
  // Application Details Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Application Details', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  if (applicationData.previousSchool) {
    doc.text(`Previous School: ${applicationData.previousSchool}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.previousClass) {
    doc.text(`Previous Class: ${applicationData.previousClass}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.applyingForClass) {
    doc.text(`Applying For: ${applicationData.applyingForClass}`, 20, yPos);
    yPos += 6;
  }
  
  if (applicationData.academicYear) {
    doc.text(`Academic Year: ${applicationData.academicYear}`, 20, yPos);
    yPos += 6;
  }
  yPos += 6;
  
  // Parent/Guardian Information
  if (applicationData.parentName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Parent/Guardian Information', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${applicationData.parentName}`, 20, yPos);
    yPos += 6;
    
    if (applicationData.parentPhone) {
      doc.text(`Phone: ${applicationData.parentPhone}`, 20, yPos);
      yPos += 6;
    }
    
    if (applicationData.parentEmail) {
      doc.text(`Email: ${applicationData.parentEmail}`, 20, yPos);
      yPos += 6;
    }
    
    if (applicationData.parentOccupation) {
      doc.text(`Occupation: ${applicationData.parentOccupation}`, 20, yPos);
      yPos += 6;
    }
    yPos += 6;
  }
  
  // Custom Fields Data
  if (applicationData.customFieldsData && Object.keys(applicationData.customFieldsData).length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Additional Information', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    Object.entries(applicationData.customFieldsData).forEach(([fieldLabel, fieldValue]) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      if (fieldValue.url) {
        // File/Photo upload
        doc.text(`${fieldLabel}: ${fieldValue.fileName || 'File uploaded'}`, 20, yPos);
        yPos += 6;
        doc.setFontSize(8);
        doc.text(`File URL: ${fieldValue.url.substring(0, 80)}...`, 20, yPos);
        yPos += 6;
        doc.setFontSize(10);
      } else if (fieldValue.value) {
        // Text/Number/Date field
        const value = typeof fieldValue.value === 'string' 
          ? fieldValue.value.substring(0, 100) 
          : String(fieldValue.value);
        doc.text(`${fieldLabel}: ${value}`, 20, yPos);
        yPos += 6;
      }
    });
    yPos += 6;
  }
  
  // Payment Information Section
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Information', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Transaction ID: ${transactionId}`, 20, yPos);
  yPos += 6;
  doc.text(`Application Fee: ₹${paymentData.amount || institution.applicationFee || 0}`, 20, yPos);
  yPos += 6;
  doc.text(`Payment Status: ${paymentData.status || 'Completed'}`, 20, yPos);
  yPos += 6;
  doc.text(`Payment Date: ${new Date().toLocaleDateString('en-IN', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, 20, yPos);
  yPos += 10;
  
  // Footer
  const pageHeight = doc.internal.pageSize.height;
  yPos = pageHeight - 30;
  
  doc.setLineWidth(0.5);
  doc.line(20, yPos, 190, yPos);
  yPos += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a computer-generated receipt. No signature required.', 105, yPos, { align: 'center' });
  yPos += 6;
  doc.text('Thank you for your application!', 105, yPos, { align: 'center' });
  
  // Generate PDF as blob
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  return pdfUrl;
};

